"use strict";

import { useCallback, useEffect, useRef, useState } from "react";
import { SPEED_ADJUST_BATCH, SPEED_ADJUST_BATCH_STATUS } from "../../ApiEndpoints";
import { postWithFirebaseAuth } from "../../utils/firebaseApiClient";

export const BULK_SPEED_POLL_MS = 3000;

// Counts for the end-of-run summary.
export const summarizeBulkSpeedRun = (run) => {
    const lines = Array.isArray(run?.lines) ? run.lines : [];
    return {
        adjusted: run?.adjusted ?? 0,
        skipped: run?.skipped ?? 0,
        failed: run?.failed ?? 0,
        stillFlagged: lines.filter((line) => line.status === "adjusted" && line.stillFlagged)
            .length,
    };
};

export const pendingBulkSpeedLineIds = (run) =>
    new Set(
        run?.state === "running"
            ? (run.lines || []).filter((line) => line.status === "pending").map((line) => line.dbid)
            : [],
    );

// Starts a bulk speed run for the project and follows it until it ends. A run
// started elsewhere (another tab, before a reload) is picked up on mount.
export const useBulkSpeedRun = ({ projectId, onFinished }) => {
    const [run, setRun] = useState(null);
    const [starting, setStarting] = useState(false);
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;

    const fetchStatus = useCallback(async () => {
        const response = await postWithFirebaseAuth(SPEED_ADJUST_BATCH_STATUS, { projectId });
        return response.data;
    }, [projectId]);

    useEffect(() => {
        if (!projectId) {
            return undefined;
        }
        let cancelled = false;
        fetchStatus()
            .then((status) => {
                if (!cancelled && status?.state === "running") {
                    setRun(status);
                }
            })
            .catch((error) => console.error("Could not read the speed adjustment status", error));
        return () => {
            cancelled = true;
        };
    }, [fetchStatus, projectId]);

    const running = run?.state === "running";
    const runId = run?.runId;

    useEffect(() => {
        if (!running) {
            return undefined;
        }
        let cancelled = false;
        const timer = setInterval(async () => {
            try {
                const status = await fetchStatus();
                if (cancelled || status?.runId !== runId) {
                    return;
                }
                setRun(status);
                if (status.state !== "running") {
                    onFinishedRef.current?.(status);
                }
            } catch (error) {
                console.error("Could not read the speed adjustment status", error);
            }
        }, BULK_SPEED_POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [fetchStatus, running, runId]);

    // Resolves to null when the run started, or to the reason it did not.
    const start = useCallback(
        async ({ languageCode, dialogueIds, speed }) => {
            setStarting(true);
            try {
                const response = await postWithFirebaseAuth(SPEED_ADJUST_BATCH, {
                    projectId,
                    languageCode,
                    dialogueIds,
                    speed,
                });
                setRun({
                    runId: response.data.runId,
                    state: "running",
                    languageCode,
                    speed,
                    total: dialogueIds.length,
                    adjusted: 0,
                    skipped: 0,
                    failed: 0,
                    lines: dialogueIds.map((dbid) => ({ dbid, status: "pending" })),
                });
                return null;
            } catch (error) {
                if (error.response?.status === 409) {
                    return "A speed adjustment is already running for this project.";
                }
                return error.response?.data?.error || "The speed adjustment could not be started.";
            } finally {
                setStarting(false);
            }
        },
        [projectId],
    );

    return { run, running, starting, start };
};
